import {
  ArrowLeftCircleIcon,
  ArrowRightCircleIcon,
} from "@heroicons/react/24/solid";
import { useIntersectionObserver } from "@react-hookz/web";
import classNames from "classnames";
import { useRef } from "react";

/**
 *
 * html structure needs to be:
 *
 * <div className="relative" ref={scrollContainerRef}> // out parent that is set to relative where the gradients will stick to
 *   <div className="overflow-auto"> // the actual scroll container
 *     {topSensor}
 *
 *     // content
 *
 *     {bottomSensor}
 *   </div>
 *
 *   {gradients} // gradients NOT in the scroll container, but in the parent container so they stick and aren't scrolled
 * </div>
 *
 *
 */
export default function useAutoOverflowScrollContainerGradients<
  Container extends HTMLElement
>() {
  const scrollContainerRef = useRef<Container>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrolledToTop = useIntersectionObserver(topRef, {
    root: scrollContainerRef,
  });
  const scrolledToBottom = useIntersectionObserver(bottomRef, {
    root: scrollContainerRef,
  });
  const showTopGradient = scrolledToTop ? !scrolledToTop.isIntersecting : false;
  const showBottomGradient = scrolledToBottom
    ? !scrolledToBottom?.isIntersecting
    : false;

  return {
    scrollContainerRef,
    topSensor: <div className="h-0.5" ref={topRef} />,
    bottomSensor: <div className="h-0.5" ref={bottomRef} />,
    gradients: (
      <>
        <div
          className={classNames(
            "pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-neutral-900 to-transparent transition-opacity",
            showTopGradient ? "opacity-100" : "opacity-0"
          )}
        />

        <div
          className={classNames(
            "pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-neutral-900 to-transparent transition-opacity",
            showBottomGradient ? "opacity-100" : "opacity-0"
          )}
        />
      </>
    ),
  };
}

export function useHorizontalAutoOverflowScrollContainerGradients<
  Container extends HTMLElement
>() {
  const scrollContainerRef = useRef<Container>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const scrolledToLeft = useIntersectionObserver(leftRef, {
    root: scrollContainerRef,
  });
  const scrolledToRight = useIntersectionObserver(rightRef, {
    root: scrollContainerRef,
  }) ?? { isIntersecting: false }; // default to showing the right gradient on page load it looks better then it coming in later which it does FIXME: maybe it is possible to check on mount if it is intersecting which would show up instantly instead of a couple sec delay
  const showLeftGradient = scrolledToLeft
    ? !scrolledToLeft.isIntersecting
    : false;
  const showRightGradient = scrolledToRight
    ? !scrolledToRight?.isIntersecting
    : false;

  return {
    scrollContainerRef,
    leftSensor: (
      <div
        style={{ width: 1, height: 1 }}
        className="inline-block"
        ref={leftRef}
      />
    ),
    rightSensor: (
      <div
        style={{ width: 1, height: 1 }}
        className="inline-block"
        ref={rightRef}
      />
    ),
    gradients: (
      <>
        <div
          className={classNames(
            "pointer-events-none absolute inset-y-0 left-0 w-14 bg-gradient-to-r from-black to-transparent transition-opacity flex items-center justify-center",
            showLeftGradient ? "opacity-100" : "opacity-0"
          )}
        >
          <ArrowLeftCircleIcon className="fill-white h-10" />
        </div>

        <div
          className={classNames(
            "pointer-events-none absolute inset-y-0 right-0 w-14 bg-gradient-to-l from-black to-transparent transition-opacity flex items-center justify-center",
            showRightGradient ? "opacity-100" : "opacity-0"
          )}
        >
          <ArrowRightCircleIcon className="fill-white h-10" />
        </div>
      </>
    ),
  };
}
